/**
 * Request preferences — the visitor's locale + timezone, resolved ONCE at the request
 * boundary and used identically by the server and the client so SSR and hydration
 * render byte-for-byte the same localized dates and numbers.
 *
 * This is the framework-recommended fix for locale/timezone hydration mismatches (see
 * TanStack Start's "Hydration Errors" guide): "pick a deterministic locale/time zone on
 * the server and use the same on the client", sourced from a cookie (preferred) or the
 * `Accept-Language` header — NOT per-component mount guards or `suppressHydrationWarning`.
 *
 * This module is PURE (no framework/server imports) so it's safe in the client bundle
 * and unit-testable. The server function that reads the actual request lives in
 * `preferences.query.ts`; the React provider + formatters' hook live in
 * `preferences-context.tsx`.
 */

export interface RequestPreferences {
  /** BCP 47 locale for `Intl` formatting, e.g. `en-US`. */
  readonly locale: string;
  /** IANA timezone, e.g. `America/New_York`. */
  readonly timeZone: string;
}

/** The safe fallback when nothing is known yet (first visit, no cookie, no geo hint). */
export const DEFAULT_PREFERENCES: RequestPreferences = { locale: 'en-US', timeZone: 'UTC' };

/** Cookie names the client writes and the server reads (see `preferences-context.tsx`). */
export const TZ_COOKIE = 'tz';
export const LOCALE_COOKIE = 'locale';

/** React Query key for the request-resolved preferences (dehydrated SSR → client). */
export const PREFERENCES_QUERY_KEY = ['request-preferences'] as const;

/** Parse a `Cookie` header into a plain map (last value wins). */
export function parseCookieHeader(header: string | null | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  if (!header) return out;
  for (const pair of header.split(';')) {
    const eq = pair.indexOf('=');
    if (eq === -1) continue;
    const key = pair.slice(0, eq).trim();
    if (!key) continue;
    try {
      out[key] = decodeURIComponent(pair.slice(eq + 1).trim());
    } catch {
      out[key] = pair.slice(eq + 1).trim();
    }
  }
  return out;
}

/**
 * Whether `tz` is a valid IANA timezone the runtime accepts. Cookie/geo input is
 * untrusted — an unknown zone makes `Intl.DateTimeFormat` THROW, so every consumer must
 * gate on this. Empty/`undefined` is invalid.
 */
export function isValidTimeZone(tz: string | undefined | null): tz is string {
  if (!tz) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/** Whether `locale` is a well-formed, runtime-accepted BCP 47 tag. */
export function isValidLocale(locale: string | undefined | null): locale is string {
  if (!locale) return false;
  try {
    // Throws RangeError on a malformed tag.
    return Intl.DateTimeFormat.supportedLocalesOf([locale]).length >= 0 && /^[a-z]{2,3}(-[A-Za-z0-9]{2,8})*$/i.test(locale);
  } catch {
    return false;
  }
}

/**
 * The first (highest-priority) language tag from an `Accept-Language` header, or null.
 * `en-US,en;q=0.9,fr;q=0.8` → `en-US`. Ignores `*` and q-values.
 */
export function parseAcceptLanguage(header: string | null | undefined): string | null {
  if (!header) return null;
  for (const part of header.split(',')) {
    const tag = part.split(';')[0]?.trim();
    if (tag && tag !== '*' && isValidLocale(tag)) return tag;
  }
  return null;
}

/**
 * Resolve the effective preferences from the request signals, in priority order:
 *  - timeZone: explicit `tz` cookie → Cloudflare geo (`request.cf.timezone`) → UTC.
 *  - locale:   explicit `locale` cookie → first `Accept-Language` tag → en-US.
 * The cookie always wins because it's the visitor's exact browser value (written by the
 * client on first visit); the geo/header are first-visit hints only.
 */
export function resolvePreferences(input: {
  readonly cookieHeader?: string | null;
  readonly acceptLanguage?: string | null;
  readonly cfTimeZone?: string | null;
}): RequestPreferences {
  const cookies = parseCookieHeader(input.cookieHeader);
  const cookieTz = cookies[TZ_COOKIE];
  const cookieLocale = cookies[LOCALE_COOKIE];

  const timeZone = isValidTimeZone(cookieTz)
    ? cookieTz
    : isValidTimeZone(input.cfTimeZone)
      ? input.cfTimeZone
      : DEFAULT_PREFERENCES.timeZone;

  const locale = isValidLocale(cookieLocale)
    ? cookieLocale
    : parseAcceptLanguage(input.acceptLanguage) ?? DEFAULT_PREFERENCES.locale;

  return { locale, timeZone };
}

export type DateVariant = 'datetime' | 'date';

const DATE_OPTS: Record<DateVariant, Intl.DateTimeFormatOptions> = {
  datetime: { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' },
  date: { year: 'numeric', month: 'short', day: 'numeric' },
};

/**
 * Format an ISO timestamp in the given preferences — hydration-safe.
 *
 * Uses `Intl` (respecting the locale's part values AND ordering) via `formatToParts`,
 * then NORMALIZES every whitespace character in the literal separators to a plain ASCII
 * space. That last step is what makes it byte-identical across the server (workerd) and
 * the browser even when their ICU versions disagree on the separator before AM/PM (ICU
 * 72+ emits a U+202F narrow-no-break-space; older emit a regular space) — that lone
 * character difference is otherwise a hydration mismatch. Returns the raw input for an
 * unparseable date rather than "Invalid Date".
 */
export function formatDateTime(iso: string, prefs: RequestPreferences, variant: DateVariant = 'datetime'): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const opts: Intl.DateTimeFormatOptions = { ...DATE_OPTS[variant], timeZone: prefs.timeZone };
  const parts = new Intl.DateTimeFormat(prefs.locale, opts).formatToParts(date);
  return parts.map((p) => (p.type === 'literal' ? p.value.replace(/\s/g, ' ') : p.value)).join('');
}

/**
 * Format a number in the given preferences' locale — deterministic across server/client
 * because both use the SAME explicit locale (a bare `toLocaleString()` uses the runtime's
 * default locale, which differs server-vs-client and mismatches). Grouping separators are
 * normalized to ASCII for the same ICU-version reason as dates.
 */
export function formatNumber(value: number, prefs: RequestPreferences, opts?: Intl.NumberFormatOptions): string {
  const parts = new Intl.NumberFormat(prefs.locale, opts).formatToParts(value);
  return parts.map((p) => (p.type === 'literal' || p.type === 'group' ? p.value.replace(/\s/g, ' ') : p.value)).join('');
}

/**
 * The browser's actual locale + timezone (client-only). Used by the reconciler to write
 * the cookie so the NEXT SSR renders exactly right. Falls back to defaults where the APIs
 * are unavailable.
 */
export function detectBrowserPreferences(): RequestPreferences {
  let timeZone = DEFAULT_PREFERENCES.timeZone;
  let locale = DEFAULT_PREFERENCES.locale;
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (isValidTimeZone(tz)) timeZone = tz;
  } catch {
    /* keep default */
  }
  try {
    const nav = typeof navigator !== 'undefined' ? navigator.language : null;
    if (isValidLocale(nav)) locale = nav;
  } catch {
    /* keep default */
  }
  return { locale, timeZone };
}
